/**
 * Post-auth return helpers — shared by LandingPage (consumes) and the
 * marketing-driven entry points (EventCheckoutPage, the Astro register page's
 * deep links) which stash where to send the user after a successful Auth0
 * round-trip.
 *
 * Two carriers:
 *
 *  1. `?returnTo=` on the URL — survives a navigation, NOT an Auth0
 *     redirect (Auth0 always rewrites the URL to its own callback shape).
 *  2. sessionStorage under `POST_AUTH_RETURN_KEY` — set right before
 *     loginWithRedirect() fires, read on the `/` render after callback.
 *
 * Both carriers are validated against POST_AUTH_RETURN_RULES on read so a
 * tampered URL/storage value can never drop a user on an off-site or
 * privilege-escalated destination. LandingPage feeds the result straight to
 * `<Navigate to={…}>`, so this module is the whole guard.
 *
 * Query strings are allowlisted per path rather than banned outright. Banning
 * them lost the partner coupon on the event-checkout path — a visitor arriving
 * at /events/123/checkout?coupon=DOMINOS26 who signed up came back to the same
 * page at full price, because the only carrier of the campaign's discount was
 * the query string this guard threw away. Unknown params are dropped rather
 * than rejecting the whole path, so a UTM-decorated marketing link keeps the
 * params that matter instead of failing closed to the dashboard.
 */

export const POST_AUTH_RETURN_KEY = 'wz.postAuthReturnTo';

// Partner promo codes (promoConfig.ts). Deliberately narrower than "anything
// URL-safe": no %, no /, no :, so no value can smuggle a path or a scheme.
const COUPON_CODE = /^[A-Za-z0-9_-]{1,64}$/;

// Invoice-pay authorization token — kept to the exact charset it had when it
// was spelled inline in the old combined regex.
const ACCESS_KEY = /^[A-Za-z0-9]{1,128}$/;

// A course link from the marketing site's On-Demand catalog.
const COURSE_SLUG = /^[a-z0-9-]{1,120}$/;

export interface ReturnPathRule {
  /** Anchored, query-free path pattern. */
  path: RegExp;
  /** Query params this path may carry, each with its permitted value shape. */
  params?: Readonly<Record<string, RegExp>>;
}

/**
 * Same-origin paths a post-auth redirect is allowed to land on. Anything
 * not matching falls back to the default landing target (dashboard).
 */
export const POST_AUTH_RETURN_RULES: readonly ReturnPathRule[] = [
  { path: /^\/events\/\d+\/register\/?$/ },
  // The coupon IS the Domino's campaign — losing it here silently charges a
  // partner's people full price.
  { path: /^\/events\/\d+\/checkout\/?$/, params: { coupon: COUPON_CODE } },
  { path: /^\/events\/\d+\/?$/ },
  { path: /^\/events\/?$/ },
  { path: /^\/courses\/?$/, params: { course: COURSE_SLUG } },
  { path: /^\/my-courses\/?$/ },
  { path: /^\/dashboard\/?$/ },
  // Profile-completion deep link from the admin "Copy Profile Link" button —
  // an admin sends a user a link that pre-fills their email at login and drops
  // them on the profile form afterward. The email travels as the Auth0
  // login_hint, not on this return path, so no query params are allowed here.
  { path: /^\/complete-profile\/?$/ },
  // Invoice pay deep link from the marketing site's /invoice/ viewer. The
  // access_key query must survive the Auth0 round-trip — it's the checkout
  // authorization.
  { path: /^\/invoices\/\d+\/pay\/?$/, params: { access_key: ACCESS_KEY } },
];

// Characters that let a string starting with "/" still resolve off-origin or
// out of the path: backslash (browsers fold it to "/", so "/\evil.com" is
// protocol-relative), plus the fragment, whitespace and control bytes that make
// a URL split somewhere other than where the rule patterns assume. Query VALUES
// are deliberately NOT checked against this - each is already pinned to its own
// charset by the matched rule's param allowlist.
// eslint-disable-next-line no-control-regex
const UNSAFE_PATH_CHARS = /[\\#\s\u0000-\u001F\u007F]/;

/**
 * Reduce a candidate return path to the exact string that is safe to navigate
 * to, or null when the path itself is not allowed. Params outside the matched
 * rule's allowlist are dropped, so an off-site value can never ride along.
 */
export function sanitizeReturnPath(path: string | null | undefined): string | null {
  if (!path) return null;
  // Reject protocol-relative `//evil.com`, full URLs, and `javascript:`.
  if (!path.startsWith('/') || path.startsWith('//')) return null;

  const queryAt = path.indexOf('?');
  const pathname = queryAt === -1 ? path : path.slice(0, queryAt);
  const query = queryAt === -1 ? '' : path.slice(queryAt + 1);
  if (UNSAFE_PATH_CHARS.test(pathname)) return null;

  const rule = POST_AUTH_RETURN_RULES.find((r) => r.path.test(pathname));
  if (!rule) return null;

  return pathname + allowedQuery(query, rule.params);
}

// Keeps the first occurrence of each allowlisted param whose value matches its
// pattern; everything else is discarded. Returns "" or "?a=1&b=2".
function allowedQuery(query: string, allowed: ReturnPathRule['params']): string {
  if (!query || !allowed) return '';

  const kept: string[] = [];
  const seen = new Set<string>();
  for (const pair of query.split('&')) {
    const eq = pair.indexOf('=');
    const name = eq === -1 ? pair : pair.slice(0, eq);
    const value = eq === -1 ? '' : pair.slice(eq + 1);
    const pattern = Object.prototype.hasOwnProperty.call(allowed, name) ? allowed[name] : undefined;
    if (!pattern || seen.has(name) || !pattern.test(value)) continue;
    seen.add(name);
    kept.push(`${name}=${value}`);
  }
  return kept.length > 0 ? `?${kept.join('&')}` : '';
}

/**
 * True only when `path` is safe to navigate to VERBATIM. A path whose query
 * carries anything outside its rule's allowlist is false here even though
 * `sanitizeReturnPath` can salvage it — callers that navigate to the string
 * they tested must not be handed a value that still needs stripping.
 */
export function isAllowedReturnPath(path: string | null | undefined): path is string {
  return typeof path === 'string' && sanitizeReturnPath(path) === path;
}

/**
 * Stash a `returnTo` for the post-Auth0-callback step. No-op when the path
 * doesn't pass the allowlist (so callers can pass user input safely).
 */
export function stashReturnTo(path: string | null | undefined): void {
  const safe = sanitizeReturnPath(path);
  if (!safe) return;
  try {
    sessionStorage.setItem(POST_AUTH_RETURN_KEY, safe);
  } catch {
    // sessionStorage can throw in private-browsing modes on some Safari
    // versions. Failing silently is correct — the user just lands on the
    // dashboard fallback instead of the intended return path.
  }
}

/**
 * The return path to use, without clearing it. URL `?returnTo=` wins over
 * sessionStorage so an already-authed user clicking "Sign Up to Register"
 * from the marketing site lands on the event page even if a stale value
 * was sitting in storage from a previous session.
 *
 * Safe to call while rendering: a render React throws away (StrictMode's
 * double render, an interrupted render) must not lose the destination, so
 * clearing it is clearReturnTo's job, once the redirect has committed.
 */
export function peekReturnTo(): string | null {
  const url = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('returnTo') : null;
  let stash: string | null = null;
  try {
    stash = sessionStorage.getItem(POST_AUTH_RETURN_KEY);
  } catch {
    // ignore — see stashReturnTo
  }
  return sanitizeReturnPath(url) ?? sanitizeReturnPath(stash);
}

export function clearReturnTo(): void {
  try {
    sessionStorage.removeItem(POST_AUTH_RETURN_KEY);
  } catch {
    // ignore — see stashReturnTo
  }
}

/** Read-and-clear, for callers outside render (an event handler or effect). */
export function consumeReturnTo(): string | null {
  const path = peekReturnTo();
  clearReturnTo();
  return path;
}
